import { useState, useEffect, useMemo } from "react";
import { useMode } from "@/context/ModeContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Calendar as CalendarIcon, List, Search } from "lucide-react";
import { CalendarView } from "@/components/CalendarView";
import { TimelineView } from "@/components/TimelineView";
import { AppointmentFormDialog } from "@/components/AppointmentFormDialog";
import { AddPatientDialog } from "@/components/AddPatientDialog";
import { useAppointmentsData } from "@/hooks/features/useAppointmentsData";
import { useFeeAgreementLogic } from "@/hooks/features/useFeeAgreementLogic";

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { syncService, buildAppointmentRollbackData } from "../services/SyncService";
import { MobileHeader } from "@/components/MobileHeader";

import { useQuota } from "../hooks/useQuota";
import { toPaise, isWholeNumber } from "../utils/currency";
import { useUserRole } from "../hooks/useUserRole";
import { getAppointmentAccess } from "../utils/accessPolicy";
import PropTypes from 'prop-types';
import { ReticleSignal, signal } from "@/reticle";


export default function Appointments({ token, onLogout }) {
  const { isReceptionistOnly, memberships } = useUserRole();
  const getAccessForAppointment = (appointment) =>
    getAppointmentAccess(appointment, localStorage.getItem("user_id"), memberships);
  // ... (rest of file)

  // (At the end)
  Appointments.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
  };
  const navigate = useNavigate();
  const { activeMode } = useMode();
  const [showDialog, setShowDialog] = useState(false);
  const [showAddPatientDialog, setShowAddPatientDialog] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: "",
    date_time: "",
    fee_amount: "",
    status: "scheduled",
    notes: "",
    duration_minutes: "", // Will default from settings
    clinic_id: "", // Empty string = personal appointment
    selected_doctor_id: "", // For clinic appointments - which doctor is this for
    skip_facility_charge: false,
    override_charge_amount: "",
    fee_agreement_id: "" // Selected rule
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'timeline'

  // Cancellation dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');

  const [selectedDate, setSelectedDate] = useState(new Date());

  const doctorId = localStorage.getItem('user_id');
  const appointmentsQuota = useQuota('appointments', (activeMode && activeMode !== 'personal' && activeMode !== 'everything') ? activeMode : null);

  // Get user settings for default appointment duration
  const dbSettings = useLiveQuery(() => doctorId ? db.settings.get(doctorId) : null, [doctorId]);

  // Restore queries for dependencies
  const allPatients = useLiveQuery(() => db.patients.toArray()) || [];

  const activePatients = useMemo(() =>
    allPatients.filter(p => !p.deleted),
    [allPatients]);

  const allDoctors = useLiveQuery(() => db.users.toArray()) || [];

  // Handle global patient selection - save to local DB  
  const handlePatientFromServer = async (patient) => {
    // Add patient to local DB for display purposes
    try {
      const existing = await db.patients.get(patient.id);
      if (!existing) {
        await db.patients.put({
          ...patient,
          treated_by_doctor_id: patient.treated_by_doctor_id || doctorId,
          _synced: true // Already exists on server
        });
      }
    } catch (error) {
      console.error("Failed to cache patient:", error);
    }
  };

  // Handle newly created patient from Add Patient dialog
  const handlePatientCreated = (patient) => {
    setFormData(prev => ({ ...prev, patient_id: patient.id }));
    toast.success(`Patient "${patient.name}" created and selected!`);
  };

  // Fetch and filter appointments using the feature hook
  const filteredAppointments = useAppointmentsData(doctorId, activeMode, searchTerm, allPatients);

  // Restore Clinic Logic (Needed for AppointmentFormDialog)
  // Fetch clinics user is a member of
  const clinics = useLiveQuery(async () => {
    if (!doctorId) return [];
    const memberships = await db.clinic_members
      .where('user_id').equals(doctorId)
      .filter(m => m.is_active !== false)
      .toArray();
    const clinicIds = memberships.map(m => m.clinic_id);
    return db.clinics.where('id').anyOf(clinicIds).toArray();
  }, [doctorId]) || [];

  // Determine user's role for the selected clinic
  const myMemberships = useLiveQuery(async () => {
    if (!doctorId) return [];
    return db.clinic_members
      .where('user_id').equals(doctorId)
      .toArray();
  }, [doctorId]) || [];

  // Get my role for the currently selected clinic
  const myRoleForSelectedClinic = useMemo(() => {
    if (!formData.clinic_id) return null;
    const membership = myMemberships.find(m => m.clinic_id === formData.clinic_id);
    return membership?.role;
  }, [formData.clinic_id, myMemberships]);

  // Check if current user needs to select a doctor (receptionist/admin selecting for others)
  const needsDoctorSelector = myRoleForSelectedClinic === 'receptionist' || myRoleForSelectedClinic === 'admin';

  // Fetch doctors for selected clinic (only doctors/admins, not receptionists)
  const [clinicDoctors, setClinicDoctors] = useState([]);

  useEffect(() => {
    const fetchClinicDoctors = async () => {
      if (!formData.clinic_id) {
        setClinicDoctors([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/clinics/${formData.clinic_id}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Filter to only doctors, consultants and admins (not receptionists)
        const doctors = response.data.filter(m => ['doctor', 'consultant', 'admin'].includes(m.role));
        setClinicDoctors(doctors);
      } catch (error) {
        console.error("Failed to fetch clinic doctors:", error);
        setClinicDoctors([]);
      }
    };
    fetchClinicDoctors();
  }, [formData.clinic_id, token]);

  // Determine if user is a doctor/admin (for showing facility_charge info)
  const myRoles = useLiveQuery(async () => {
    if (!doctorId) return [];
    const memberships = await db.clinic_members
      .where('user_id').equals(doctorId)
      .toArray();
    return memberships.map(m => m.role);
  }, [doctorId]) || [];

  const isDoctor = !clinics.length || myRoles.includes('admin') || myRoles.includes('doctor');

  // The hook returns the final filtered list, so we treat it as 'appointments' for legacy references if needed,
  // but better to just use filteredAppointments directly.
  const appointments = filteredAppointments; // Alias if we need the 'raw' list for missing patient check logic below

  // Fallback: Check for appointments where patient is missing from local DB and fetch them
  useEffect(() => {
    if (!appointments.length || !token) return;

    const fetchMissingPatients = async () => {
      // Find patient IDs that are in appointments but not in allPatients
      const knownPatientIds = new Set(allPatients.map(p => p.id));
      const missingPatientIds = new Set();

      for (const apt of appointments) {
        if (apt.patient_id && !knownPatientIds.has(apt.patient_id)) {
          missingPatientIds.add(apt.patient_id);
        }
      }

      if (missingPatientIds.size === 0) return;

      console.log("Fetching missing patients for clinic appointments:", Array.from(missingPatientIds));

      // Fetch each missing patient
      for (const patientId of missingPatientIds) {
        try {
          const response = await axios.get(`${API}/patients/${patientId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.data && response.data.patient) {
            // Save to local DB with proper _synced flag so it persists
            const patientData = { ...response.data.patient, _synced: true };

            // Allow this patient to be visible even if treated by other doctor
            // (The server already verified we have permission to see them via appointment)
            await db.patients.put(patientData);
          }
        } catch (error) {
          console.error(`Failed to fetch missing patient ${patientId}:`, error);
        }
      }
    };

    // Debounce slightly to avoid rapid firing on load
    const timeoutId = setTimeout(fetchMissingPatients, 1000);
    return () => clearTimeout(timeoutId);
  }, [appointments, allPatients, token]);

  // Calculate facility_charge preview
  const selectedPatient = useMemo(() =>
    activePatients.find(p => p.id === formData.patient_id)
    , [activePatients, formData.patient_id]);

  // Calculate facility_charge logic using feature hook
  const { bestRule, referralDoctor, calculatedCharge } = useFeeAgreementLogic(
    doctorId,
    formData.patient_id,
    formData.clinic_id,
    formData.fee_amount,
    selectedPatient,
    allDoctors
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate whole numbers
    if (!isWholeNumber(formData.fee_amount)) {
      toast.error("Fee amount must be a whole number (no decimals)");
      return;
    }
    if (formData.override_charge_amount && !isWholeNumber(formData.override_charge_amount)) {
      toast.error("Facility charge amount must be a whole number (no decimals)");
      return;
    }

    // Validate doctor selection for clinic appointments
    if (needsDoctorSelector && formData.clinic_id && !formData.selected_doctor_id) {
      toast.error("Please select a doctor for this appointment");
      return;
    }

    try {
      // Use selected doctor for clinic appointments, otherwise current user
      const appointmentDoctorId = (needsDoctorSelector && formData.selected_doctor_id)
        ? formData.selected_doctor_id
        : doctorId;

      const newAppointment = {
        id: crypto.randomUUID(),
        doctor_id: appointmentDoctorId,
        patient_id: formData.patient_id,
        clinic_id: formData.clinic_id || null, // null for personal
        date_time: formData.date_time,
        fee_amount: toPaise(formData.fee_amount),  // Convert rupees to paise
        duration_minutes: parseInt(formData.duration_minutes) || 30,
        status: "scheduled",
        notes: formData.notes,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        _synced: false // Mark as unsynced until backend confirms
      };

      await db.appointments.add(newAppointment);

      // Include skip_facility_charge and override in sync payload for backend
      const syncPayload = {
        ...newAppointment,
        skip_facility_charge: formData.skip_facility_charge,
        override_charge_amount: formData.override_charge_amount ? toPaise(formData.override_charge_amount) : null,
        fee_agreement_id: bestRule ? bestRule.id : null
      };
      await syncService.addToQueue('create', 'appointment', syncPayload);

      // Optimistically create facility_charge if:
      // 1. User is a doctor/admin (has permission)
      // 2. It's a personal appointment (not clinic - for clinic, backend handles with correct doctor)
      // 3. Patient has referral info and facility_charge not skipped
      const isPersonalAppointment = !formData.clinic_id;

      if (isDoctor && isPersonalAppointment && bestRule && !formData.skip_facility_charge) {
        let chargeAmount = 0;

        if (formData.override_charge_amount && parseInt(formData.override_charge_amount) > 0) {
          chargeAmount = toPaise(formData.override_charge_amount);
        } else if (bestRule.rule_type === 'percent') {
          chargeAmount = toPaise(formData.fee_amount) * bestRule.fee_value / 100;
        } else {
          chargeAmount = bestRule.fee_value;
        }

        const newCharge = {
          id: `comm_${newAppointment.id}`,
          appointment_id: newAppointment.id,
          associated_doctor_id: appointmentDoctorId, // The doctor being charged
          clinic_id: bestRule.clinic_id || null,
          charge_amount: chargeAmount,
          paid_status: 'pending',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          _synced: false // Mark as unsynced until backend confirms
        };

        await db.facility_charges.add(newCharge);
        // Queue facility_charge creation so it exists on backend before payment
        await syncService.addToQueue('create', 'facility_charge', newCharge);
      }

      signal(ReticleSignal.APPOINTMENT_CREATED, {
        appointmentId: newAppointment.id,
        patientId: newAppointment.patient_id,
        doctorId: newAppointment.doctor_id,
        clinicId: newAppointment.clinic_id,
        actorId: doctorId,
        hasFacilityCharge: Boolean(bestRule && !formData.skip_facility_charge)
      });

      toast.success("Appointment created successfully!");
      setShowDialog(false);
      setFormData({
        patient_id: "", date_time: "", fee_amount: "", status: "scheduled", notes: "",
        duration_minutes: dbSettings?.default_appointment_duration || 30,
        clinic_id: "", selected_doctor_id: "", skip_facility_charge: false, override_charge_amount: "", fee_agreement_id: ""
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to create appointment");
    }
  };

  const updateStatus = async (appointmentId, newStatus, cancellationReasonParam = null) => {
    try {
      const apt = await db.appointments.get(appointmentId);
      const access = getAccessForAppointment(apt);
      if (!access.canEdit) {
        toast.error("You do not have permission to edit this appointment");
        return;
      }

      const timestamp = new Date().toISOString();
      const updateFields = {
        status: newStatus,
        updated_at: timestamp
      };

      // Include cancellation reason if cancelling with a reason
      if (newStatus === 'cancelled' && cancellationReasonParam) {
        updateFields.cancellation_reason = cancellationReasonParam;
      }

      const existingCharge = await db.facility_charges.where('appointment_id').equals(appointmentId).first();
      const rollbackData = buildAppointmentRollbackData(
        apt,
        ["status", "cancellation_reason"],
        existingCharge
      );

      await db.appointments.update(appointmentId, updateFields);

      // If appointment is cancelled, delete the corresponding facility_charge
      if (newStatus === 'cancelled') {
        await syncService.addToQueue('update', 'appointment', {
          id: appointmentId,
          status: newStatus,
          updated_at: timestamp,
          ...(cancellationReasonParam ? { cancellation_reason: cancellationReasonParam } : {})
        }, { rollbackData, processImmediately: false });

        if (existingCharge) {
          await db.facility_charges.delete(existingCharge.id);
          await syncService.addToQueue('delete', 'facility_charge', { id: existingCharge.id }, { processImmediately: false });
        }
        syncService.processQueue();
      } else {
        await syncService.addToQueue('update', 'appointment', {
          id: appointmentId,
          status: newStatus,
          updated_at: timestamp
        }, { rollbackData });
      }

      signal(ReticleSignal.APPOINTMENT_STATUS_UPDATED, {
        appointmentId,
        actorId: doctorId,
        status: newStatus
      });

      toast.success("Status updated");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    }
  };

  // Handler to initiate cancellation (shows dialog)
  const handleCancelAppointment = (appointmentId) => {
    setCancellingAppointmentId(appointmentId);
    setCancellationReason('');
    setShowCancelDialog(true);
  };

  // Handler to confirm cancellation with optional reason
  const handleConfirmCancellation = async () => {
    if (cancellingAppointmentId) {
      await updateStatus(cancellingAppointmentId, 'cancelled', cancellationReason || null);
    }
    setShowCancelDialog(false);
    setCancellingAppointmentId(null);
    setCancellationReason('');
  };

  // Wrapper for onStatusUpdate that intercepts cancel requests
  const handleStatusUpdate = (appointmentId, newStatus) => {
    if (newStatus === 'cancelled') {
      handleCancelAppointment(appointmentId);
    } else {
      updateStatus(appointmentId, newStatus);
    }
  };

  // Handler for clicking on empty timeline slot - opens form with pre-filled time
  const handleSlotClick = (dateTime) => {
    // Check Quota
    if (!appointmentsQuota.allowed) {
      toast.error("Appointment limit reached for this month. Please upgrade your plan.");
      return;
    }

    setFormData(prev => ({
      ...prev,
      date_time: dateTime,
      clinic_id: (activeMode && activeMode !== 'personal' && activeMode !== 'everything') ? activeMode : ""
    }));
    setShowDialog(true);
  };

  // Handler for drag-and-drop reschedule
  const handleReschedule = async (appointmentId, newDateTime) => {
    try {
      const apt = await db.appointments.get(appointmentId);
      const access = getAccessForAppointment(apt);
      if (!access.canEdit) {
        toast.error("You do not have permission to edit this appointment");
        return;
      }

      const timestamp = new Date().toISOString();
      const rollbackData = buildAppointmentRollbackData(apt, ["date_time"]);

      // Update local DB
      await db.appointments.update(appointmentId, {
        date_time: newDateTime,
        updated_at: timestamp
      });

      // Queue sync
      await syncService.addToQueue('update', 'appointment', {
        id: appointmentId,
        date_time: newDateTime,
        updated_at: timestamp
      }, { rollbackData });

      toast.success("Appointment rescheduled");
    } catch (error) {
      console.error(error);
      toast.error("Failed to reschedule appointment");
    }
  };

  // Handler for resize (change duration)
  const handleResize = async (appointmentId, newDuration) => {
    try {
      const apt = await db.appointments.get(appointmentId);
      const access = getAccessForAppointment(apt);
      if (!access.canEdit) {
        toast.error("You do not have permission to edit this appointment");
        return;
      }

      const timestamp = new Date().toISOString();
      const rollbackData = buildAppointmentRollbackData(apt, ["duration_minutes"]);

      // Update local DB
      await db.appointments.update(appointmentId, {
        duration_minutes: newDuration,
        updated_at: timestamp
      });

      // Queue sync
      await syncService.addToQueue('update', 'appointment', {
        id: appointmentId,
        duration_minutes: newDuration,
        updated_at: timestamp
      }, { rollbackData });

      toast.success(`Duration updated to ${newDuration} min`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update duration");
    }
  };

  return (
    <div className="page-container" data-testid="appointments-page">
      <Sidebar active="appointments" onLogout={onLogout} />

      <div className="main-content overflow-x-hidden max-w-full">
        <div className="header hidden md:flex">
          <h1>Appointments</h1>
          <div className="flex items-center justify-between w-full sm:w-auto gap-2 sm:gap-4 flex-wrap">

            <Button
              data-testid="add-appointment-button"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  clinic_id: (activeMode && activeMode !== 'personal' && activeMode !== 'everything') ? activeMode : ""
                }));
                // Check Quota
                if (!appointmentsQuota.allowed) {
                  toast.error("Appointment limit reached for this month. Please upgrade your plan.");
                  return;
                }
                setShowDialog(true);
              }}
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
            >
              <Plus size={20} style={{ marginRight: '0.5rem' }} />
              Add Appointment
            </Button>
          </div>
        </div>

        <MobileHeader title="Appointments">
          <Button
            className="w-full mt-2"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                clinic_id: (activeMode && activeMode !== 'personal' && activeMode !== 'everything') ? activeMode : ""
              }));
              setShowDialog(true);
            }}
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}
          >
            <Plus size={20} className="mr-2" />
            Add Appointment
          </Button>
        </MobileHeader>

        <div className="search-bar mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by patient name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="appointment-search-input"
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-end gap-2 mb-4">
          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('calendar')}
            className="gap-1"
          >
            <CalendarIcon size={16} />
            Calendar
          </Button>
          <Button
            variant={viewMode === 'timeline' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('timeline')}
            className="gap-1"
          >
            <List size={16} />
            Timeline
          </Button>
        </div>

        {viewMode === 'calendar' ? (
          <CalendarView
            appointments={filteredAppointments}
            allPatients={allPatients}
            allDoctors={allDoctors}
            clinics={clinics}
            onAppointmentClick={(id) => navigate(`/appointments/${id}`)}
            onStatusUpdate={handleStatusUpdate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            getAppointmentAccess={getAccessForAppointment}
          />
        ) : (
          <TimelineView
            appointments={filteredAppointments}
            allPatients={allPatients}
            allDoctors={allDoctors}
            clinics={clinics}
            onAppointmentClick={(id) => navigate(`/appointments/${id}`)}
            onStatusUpdate={handleStatusUpdate}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onSlotClick={handleSlotClick}
            onAppointmentReschedule={handleReschedule}
            onAppointmentResize={handleResize}
            getAppointmentAccess={getAccessForAppointment}
          />
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <AppointmentFormDialog
            open={showDialog}
            onOpenChange={setShowDialog}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            activePatients={activePatients}
            clinics={clinics}
            clinicDoctors={clinicDoctors}
            isReceptionistOnly={isReceptionistOnly}
            needsDoctorSelector={needsDoctorSelector}
            handlePatientFromServer={handlePatientFromServer}
            token={token}
            dbSettings={dbSettings}
            // Facility charge props
            isDoctor={isDoctor}
            myRoleForSelectedClinic={myRoleForSelectedClinic}
            bestRule={bestRule}
            selectedPatient={selectedPatient}
            referralDoctor={referralDoctor}
            calculatedCharge={calculatedCharge}
            onAddNewPatient={() => setShowAddPatientDialog(true)}
          />
        </Dialog>

        {/* Add Patient Dialog (triggered from appointment flow) */}
        <AddPatientDialog
          open={showAddPatientDialog}
          onOpenChange={setShowAddPatientDialog}
          token={token}
          onPatientCreated={handlePatientCreated}
        />

        {/* Cancellation Reason Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Appointment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Would you like to provide a reason for cancellation? (Optional)
              </p>
              <Textarea
                placeholder="Enter cancellation reason..."
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCancellationReason('');
                  handleConfirmCancellation();
                }}
              >
                Skip
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmCancellation}
              >
                Cancel Appointment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

Appointments.propTypes = {
  token: PropTypes.string,
  onLogout: PropTypes.func
};
